import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  useApi, type EdgeInfo, type EdgeDisk, type LatestRelease,
  type EdgeReleaseHistoryEvent,
} from '../lib/api';
import { formatGB, rttColor, MetricBar, diskTextColor, DISK_WARN_PCT } from './EdgesPage';
import {
  GITHUB_REPO,
  formatRelative,
  heartbeatColor,
  isMetricsStale,
} from '../lib/edgeStatus';
import { EdgeReleaseDetails } from '../components/EdgeReleaseStatus';
import {
  ChevronLeft, RefreshCw, GitCommit, ExternalLink,
  Server, Cpu, MemoryStick, HardDrive, Activity, Globe, Network, Puzzle, Clock,
} from 'lucide-react';

/** Absolute, human-readable timestamp (local). Returns '—' for missing/invalid. */
function formatAbsolute(iso?: string): string {
  if (!iso) return '—';
  const t = new Date(iso);
  if (!Number.isFinite(t.getTime())) return '—';
  return t.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

// Friendly labels for the edge-reported integration provider keys.
const INTEGRATION_LABELS: Record<string, string> = {
  dahua: 'Dahua', dyson: 'Dyson', denon: 'Denon', kasa: 'TP-Link Kasa',
  tesla: 'Tesla', kia: 'Kia Connect', ytmusic: 'YouTube Music', hue: 'Philips Hue',
  matter: 'Matter', watermeter: 'Water meter', zigbee: 'Zigbee', leak: 'Leak detector',
  seam: 'Seam', bhyve: 'Orbit B-hyve', rainbird: 'Rain Bird', lg: 'LG ThinQ',
  garage: 'Garage door', nest: 'Nest',
};

function integrationLabel(key: string): string {
  return INTEGRATION_LABELS[key] ?? key;
}

const HISTORY_LABELS: Record<EdgeReleaseHistoryEvent['kind'], string> = {
  assigned: 'Assigned', paused: 'Paused', applying: 'Applying',
  deployed: 'Deployed', healthy: 'Healthy', rollback: 'Rollback',
  quarantined: 'Quarantined', repair_required: 'Repair required',
};

export function EdgeDetailPage() {
  const { edgeId = '' } = useParams();
  const api = useApi();
  const [edges, setEdges] = useState<EdgeInfo[] | null>(null);
  const [latestRelease, setLatestRelease] = useState<LatestRelease | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<EdgeReleaseHistoryEvent[]>([]);
  const [historyBefore, setHistoryBefore] = useState<string | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const historyRequestRef = useRef(0);
  // Skip a poll tick when the previous one is still in flight. Without this,
  // a slow `listEdges` (fleet scan) plus a slow `getLatestRelease` on the same
  // 10s cadence pile up parallel fetches and the newest sample can land behind
  // an older one, silently flipping the UI backwards (matches the fix already
  // shipped for HomesPage, HomeDetailPage, and EdgesPage).
  const inflightRef = useRef(false);

  const refresh = async () => {
    if (inflightRef.current) return;
    inflightRef.current = true;
    try {
      // Reuses the exact fleet scan the Edges page already runs — no new endpoint,
      // no extra backend cost. We just pick this edge out client-side.
      const [{ edges }, release] = await Promise.all([
        api.listEdges(),
        api.getLatestRelease().catch(() => null),
      ]);
      setEdges(edges);
      if (release) setLatestRelease(release);
      setError(null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
      inflightRef.current = false;
    }
  };

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 10000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edgeId]);

  const refreshHistory = async () => {
    const request = ++historyRequestRef.current;
    setHistory([]);
    setHistoryBefore(null);
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const page = await api.getEdgeReleaseHistory(edgeId);
      if (request !== historyRequestRef.current) return;
      setHistory(page.events);
      setHistoryBefore(page.nextBefore);
    } catch (e: any) {
      if (request === historyRequestRef.current) setHistoryError(e.message);
    } finally {
      if (request === historyRequestRef.current) setHistoryLoading(false);
    }
  };

  useEffect(() => {
    void refreshHistory();
    return () => { historyRequestRef.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edgeId]);

  const loadOlderHistory = async () => {
    if (!historyBefore || historyLoading) return;
    const request = historyRequestRef.current;
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const page = await api.getEdgeReleaseHistory(edgeId, historyBefore);
      if (request !== historyRequestRef.current) return;
      setHistory((current) => [...current, ...page.events]);
      setHistoryBefore(page.nextBefore);
    } catch (e: any) {
      if (request === historyRequestRef.current) setHistoryError(e.message);
    } finally {
      if (request === historyRequestRef.current) setHistoryLoading(false);
    }
  };

  const edge = useMemo(
    () => (edges ?? []).find((e) => e.edgeId === edgeId),
    [edges, edgeId],
  );

  const backLink = (
    <Link to="/edges" className="inline-flex items-center text-sm text-slate-600 hover:text-brand-ink mb-4">
      <ChevronLeft size={16} /> Back to edges
    </Link>
  );

  if (loading && !edges) {
    return <div>{backLink}<div className="text-slate-600 text-sm">Loading edge…</div></div>;
  }

  if (!edge) {
    return (
      <div>
        {backLink}
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
          <Server size={28} className="text-slate-400 mx-auto mb-3" />
          <div className="text-brand-ink font-medium mb-1">Edge not found</div>
          <div className="text-sm text-slate-600">
            No edge with id <span className="font-mono text-ink-800">{edgeId}</span> is visible to you.
          </div>
          {error && <div className="text-xs text-alert-breach mt-2">{error}</div>}
        </div>
      </div>
    );
  }

  const m = edge.metrics;
  const metricsStale = isMetricsStale(m?.at);
  const memPct = m && m.memTotalMB > 0 ? (m.memUsedMB / m.memTotalMB) * 100 : 0;
  const disks: EdgeDisk[] = [...(m?.disks ?? [])].sort(
    (a, b) => Number(a.external) - Number(b.external),
  );
  const release = edge.release;

  return (
    <div>
      {backLink}

      {error && (
        <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-brand-ink inline-flex items-center gap-2.5">
            <span className={`inline-block w-2.5 h-2.5 rounded-full ${heartbeatColor(edge.lastHeartbeatAt)}`} />
            {edge.hostname || 'unnamed edge'}
          </h1>
          <div className="text-xs text-slate-500 font-mono mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span title="Edge id">{edge.edgeId}</span>
            {edge.version && <span className="text-slate-600">· {edge.version}</span>}
            {edge.osVersion && <span className="text-slate-500" title={edge.osVersionAt ? `OS reported ${formatRelative(edge.osVersionAt)}` : 'OS version'}>· {edge.osVersion}</span>}
            {edge.currentBranch && edge.currentBranch !== 'stable' && (
              <span className="text-slate-500 uppercase tracking-wider not-italic">{edge.currentBranch}</span>
            )}
          </div>
        </div>
        <button
          onClick={refresh}
          className="inline-flex items-center gap-1.5 text-sm text-ink-800 hover:text-brand-ink px-3 py-1.5 rounded-lg border border-slate-300 hover:border-slate-400 shrink-0"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Quick stats */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <Stat label="Heartbeat" value={formatRelative(edge.lastHeartbeatAt)} />
        <Stat label="Deployed" value={formatRelative(edge.lastDeployAt)} />
        <Stat
          label="Release"
          value={release?.state.replace('_', ' ') ?? 'unavailable'}
          tint={release?.state === 'healthy' ? 'emerald' : release?.state === 'behind' ? 'amber' : 'ink'}
        />
        <Stat label="Paired homes" value={String(edge.pairedHomes?.length ?? 0)} />
      </div>

      {/* Software & deploy */}
      <Section title="Software & deploy" icon={<GitCommit size={18} className="text-slate-500" />}>
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          {edge.currentSha ? (
            <a
              href={`https://github.com/${GITHUB_REPO}/commit/${edge.currentSha}`}
              target="_blank"
              rel="noreferrer"
              className="font-mono text-sm text-brand-ink hover:text-slate-500 inline-flex items-center gap-1"
              title={edge.currentSha}
            >
              {edge.currentShaShort ?? edge.currentSha.slice(0, 8)} <ExternalLink size={11} className="text-slate-500" />
            </a>
          ) : (
            <span className="font-mono text-sm text-brand-ink">—</span>
          )}
          {edge.lastDeployReason && (
            <span
              className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                edge.lastDeployReason === 'rollback' || edge.lastDeployReason === 'containment-hold'
                  ? 'text-amber-700 border-amber-200 bg-amber-50'
                  : 'text-slate-600 border-slate-200 bg-slate-50'
              }`}
            >
              {edge.lastDeployReason}
            </span>
          )}
        </div>
        {edge.currentShaSubject && (
          <div className="text-sm text-brand-ink italic mb-3">"{edge.currentShaSubject}"</div>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 text-xs">
          <KV label="Branch" value={edge.currentBranch ?? 'stable'} mono />
          <KV label="OS" value={edge.osVersion ?? '—'} mono title={edge.osVersionAt ? `reported ${formatRelative(edge.osVersionAt)}` : undefined} />
          <KV label="Committed" value={formatRelative(edge.currentShaCommittedAt)} title={formatAbsolute(edge.currentShaCommittedAt)} />
          <KV label="Deployed" value={formatRelative(edge.lastDeployAt)} title={formatAbsolute(edge.lastDeployAt)} />
          {latestRelease?.manifest && (
            <KV
              label={`Latest (${latestRelease.channel})`}
              value={latestRelease.manifest.shaShort}
              mono
              title={latestRelease.manifest.subject}
            />
          )}
        </div>
      </Section>

      <Section title="Release assignment and installation" icon={<GitCommit size={18} className="text-slate-500" />}>
        <EdgeReleaseDetails release={release} />
      </Section>

      <Section title="Release history" icon={<Clock size={18} className="text-slate-500" />}>
        <button type="button" disabled={historyLoading} onClick={refreshHistory}
          className="mb-2 text-xs text-brand-ink hover:underline disabled:opacity-50">
          Refresh history
        </button>
        {historyError && <p className="mb-2 text-xs text-amber-700">{historyError}</p>}
        {history.length === 0 && !historyLoading && !historyError && (
          <p className="text-sm text-slate-500">No recorded release transitions yet.</p>
        )}
        <ol className="divide-y divide-slate-100">
          {history.map((event) => (
            <li key={event.eventKey} className="py-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
              <span className="font-medium text-brand-ink min-w-24">{HISTORY_LABELS[event.kind]}</span>
              <time className="text-slate-500" dateTime={event.at}>{formatAbsolute(event.at)}</time>
              <a href={`https://github.com/${GITHUB_REPO}/commit/${event.runtimeSha}`}
                target="_blank" rel="noreferrer" className="font-mono text-brand-ink hover:underline"
                title={event.runtimeSha}>{event.runtimeSha.slice(0, 8)}</a>
              {event.assignmentRevision !== null && (
                <span className="text-slate-500">assignment #{event.assignmentRevision}</span>
              )}
              {event.reason && <span className="text-slate-500">{event.reason}</span>}
              {event.failureCode && <span className="text-amber-700">{event.failureCode}</span>}
            </li>
          ))}
        </ol>
        {historyBefore && (
          <button type="button" disabled={historyLoading} onClick={loadOlderHistory}
            className="mt-2 text-xs text-brand-ink hover:underline disabled:opacity-50">
            {historyLoading ? 'Loading…' : 'Load older events'}
          </button>
        )}
        {history.length === 0 && historyLoading && (
          <p className="text-sm text-slate-500">Loading release history…</p>
        )}
      </Section>

      {/* Resources */}
      <Section
        title="Resources"
        icon={<Activity size={18} className="text-slate-500" />}
        right={
          m?.at ? (
            <span className={`text-xs ${metricsStale ? 'text-amber-600' : 'text-slate-500'}`}>
              Sampled {formatRelative(m.at)}{metricsStale ? ' · stale' : ''}
            </span>
          ) : (
            <span className="text-xs text-slate-500">no sample</span>
          )
        }
      >
        {!m ? (
          <div className="text-sm text-slate-500">No resource metrics reported yet.</div>
        ) : (
          <div className={`space-y-4 ${metricsStale ? 'opacity-60' : ''}`}>
            <ResourceBar
              icon={<Cpu size={13} className="text-slate-500" />}
              label="CPU"
              pct={m.cpuPct}
              stale={metricsStale}
              right={`${metricsStale ? '—' : `${Math.round(m.cpuPct)}%`}  ·  load ${m.load1.toFixed(2)}  ·  ${m.cores} cores`}
            />
            <ResourceBar
              icon={<MemoryStick size={13} className="text-slate-500" />}
              label="Memory"
              pct={memPct}
              stale={metricsStale}
              right={metricsStale ? '—' : `${formatGB(m.memUsedMB / 1024)} / ${formatGB(m.memTotalMB / 1024)} GB  ·  ${Math.round(memPct)}%`}
            />
            {m.frpsRttMs != null && (
              <div className="flex items-center gap-3" title="TCP round-trip from the edge to the frps relay — the hop off-LAN app traffic traverses">
                <Network size={13} className="text-slate-500 shrink-0" />
                <span className="text-xs text-slate-600 w-24 shrink-0">Relay RTT</span>
                <span className={`text-sm tabular-nums ${rttColor(m.frpsRttMs, metricsStale)}`}>
                  {metricsStale ? '—' : `${m.frpsRttMs} ms`}
                </span>
              </div>
            )}
            {disks.map((d) => (
              <ResourceBar
                key={d.mount}
                icon={<HardDrive size={13} className={d.external ? 'text-slate-500' : 'text-slate-500'} />}
                label={
                  <span className={d.external ? 'text-slate-600' : undefined}>
                    {d.label}{d.external && <span className="ml-1 text-slate-500 text-[10px]">·rec</span>}
                  </span>
                }
                sublabel={d.mount}
                pct={d.pct}
                stale={metricsStale}
                // Free space is the number that actually decides whether the next
                // deploy survives — `92 / 98 GB · 93%` read as "busy" on a box
                // with 1.5 GB left. Older edges don't send freeGB; fall back.
                right={
                  metricsStale
                    ? '—'
                    : `${formatGB(d.usedGB)} / ${formatGB(d.totalGB)} GB  ·  ${Math.round(d.pct)}%`
                      + (d.freeGB != null ? `  ·  ${formatGB(d.freeGB)} GB free` : '')
                }
                rightClassName={diskTextColor(d.pct, metricsStale)}
                title={
                  !metricsStale && d.pct >= DISK_WARN_PCT
                    ? `${d.mount} is ${Math.round(d.pct)}% full`
                      + (d.freeGB != null ? ` — ${formatGB(d.freeGB)} GB left` : '')
                      + '. Deploys fail part-way at 100%.'
                    : undefined
                }
              />
            ))}
          </div>
        )}
      </Section>

      {/* Two-column: Network + Integrations */}
      <div className="grid lg:grid-cols-2 gap-4 mb-4">
        <Section title="Network" icon={<Globe size={18} className="text-slate-500" />} dense>
          <div className="space-y-2 text-xs">
            <KV label="Public IP" value={edge.lastPublicIp ?? edge.publicIp ?? '—'} mono
                title={edge.lastPublicIpAt ? `updated ${formatAbsolute(edge.lastPublicIpAt)}` : undefined} />
            {edge.lastPublicIpAt && <KV label="IP updated" value={formatRelative(edge.lastPublicIpAt)} title={formatAbsolute(edge.lastPublicIpAt)} />}
            {edge.metrics?.frpsRttMs != null && (
              <KV label="Relay RTT" value={`${edge.metrics.frpsRttMs} ms`} />
            )}
          </div>
        </Section>

        <Section title="Integrations" icon={<Puzzle size={18} className="text-slate-500" />} dense
          right={edge.integrationsAt ? <span className="text-xs text-slate-500">{formatRelative(edge.integrationsAt)}</span> : undefined}
        >
          {(edge.integrations?.length ?? 0) === 0 ? (
            <div className="text-sm text-slate-500">None reported by this edge.</div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {[...edge.integrations!].sort().map((key) => (
                <span
                  key={key}
                  className="inline-flex items-center text-xs px-2 py-0.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700"
                  title={key}
                >
                  {integrationLabel(key)}
                </span>
              ))}
            </div>
          )}
        </Section>
      </div>

      {/* Paired homes */}
      <Section title="Paired homes" icon={<Network size={18} className="text-slate-500" />} dense>
        {(edge.pairedHomes?.length ?? 0) === 0 ? (
          <div className="text-sm text-slate-500">Not paired to any home.</div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {edge.pairedHomes!.map((hid) => (
              <Link
                key={hid}
                to={`/homes/${hid}`}
                className="inline-flex items-center gap-1.5 text-sm font-mono px-2.5 py-1 rounded-lg border border-slate-200 bg-slate-50 text-ink-800 hover:text-brand-ink hover:border-slate-300"
              >
                <Server size={12} className="text-slate-500" /> {hid}
              </Link>
            ))}
          </div>
        )}
      </Section>

      {/* Timeline */}
      <Section title="Timeline" icon={<Clock size={18} className="text-slate-500" />} dense>
        <div className="grid sm:grid-cols-2 gap-x-8">
          <TimeRow label="First paired" iso={edge.firstRegisteredAt ?? edge.registeredAt} />
          <TimeRow label="Last registered" iso={edge.lastRegisteredAt} />
          <TimeRow label="Commit authored" iso={edge.currentShaCommittedAt} />
          <TimeRow label="Last deploy" iso={edge.lastDeployAt} />
          <TimeRow label="Metrics sampled" iso={edge.metrics?.at} />
          <TimeRow label="Integrations reported" iso={edge.integrationsAt} />
          <TimeRow label="Public IP updated" iso={edge.lastPublicIpAt} />
          <TimeRow label="Last heartbeat" iso={edge.lastHeartbeatAt} />
        </div>
      </Section>
    </div>
  );
}

/* ---------- small presentational helpers ---------- */

function Stat({
  label, value, tint = 'ink',
}: { label: string; value: string; tint?: 'ink' | 'emerald' | 'amber' | 'red' }) {
  const tintMap: Record<string, string> = {
    ink: 'border-slate-200 bg-white text-brand-ink',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    amber: 'border-amber-200 bg-amber-50 text-amber-700',
    red: 'border-red-200 bg-red-50 text-red-700',
  };
  return (
    <div className={`rounded-xl border p-4 ${tintMap[tint]}`}>
      <div className="text-[10px] uppercase tracking-[0.16em] text-slate-600 mb-1.5">{label}</div>
      <div className="text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function Section({
  title, icon, right, children, dense = false,
}: {
  title: string;
  icon?: React.ReactNode;
  right?: React.ReactNode;
  children: React.ReactNode;
  dense?: boolean;
}) {
  return (
    <div className={`bg-white border border-slate-200 rounded-xl ${dense ? 'p-5' : 'p-6'} mb-4`}>
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-base text-brand-ink inline-flex items-center gap-2">
          {icon} {title}
        </h2>
        {right}
      </div>
      {children}
    </div>
  );
}

function KV({ label, value, mono, title }: { label: string; value: string; mono?: boolean; title?: string }) {
  return (
    <div className="flex items-center justify-between gap-3" title={title}>
      <span className="text-slate-500 shrink-0">{label}</span>
      <span className={`text-brand-ink text-right truncate ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  );
}

function ResourceBar({
  icon, label, sublabel, pct, stale, right, rightClassName, title,
}: {
  icon: React.ReactNode;
  label: React.ReactNode;
  sublabel?: string;
  pct: number;
  stale: boolean;
  right: string;
  /** Optional tint for the right-hand readout (disk pressure). */
  rightClassName?: string;
  title?: string;
}) {
  return (
    <div className="flex items-center gap-3" title={title}>
      <span className="shrink-0">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-1">
          <span className="text-xs text-ink-800 truncate">
            {label}
            {sublabel && <span className="text-slate-400 font-mono ml-1.5 text-[10px]">{sublabel}</span>}
          </span>
          <span
            className={`text-[11px] tabular-nums whitespace-nowrap ${
              rightClassName || (stale ? 'text-slate-500' : 'text-ink-800')
            }`}
          >
            {right}
          </span>
        </div>
        <MetricBar pct={pct} stale={stale} />
      </div>
    </div>
  );
}

function TimeRow({ label, iso }: { label: string; iso?: string }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5 border-b border-slate-200 text-xs">
      <span className="text-slate-500">{label}</span>
      <span className="text-brand-ink text-right" title={formatAbsolute(iso)}>{formatRelative(iso)}</span>
    </div>
  );
}
